import type {
  EvergreenConfiguration,
  EvergreenInteractionSourceInput,
  EvergreenSourceSession,
  EvergreenSourcesInput,
  EvergreenVideoSourceInput,
  PastSession,
  WebinarForEvergreen,
} from '@/features/webinar/types';
import {
  isEvergreenSourceTimelinePreparing,
  SOURCE_SESSION_REFUSALS,
} from '@/lib/evergreenConvertError';
import { resolveConversionOutcome } from '@/lib/evergreenLiveSource';
import { getBackErrorCodes } from '@/lib/evergreenInteractionSchedule';
import type { EvergreenMp4UploadError } from '@/lib/evergreenMp4File';

/** Source vidéo et source d'interactions d'un Evergreen, choisies indépendamment. */
export type EvergreenSourcesSelection = {
  video: 'LIVE' | 'MP4';
  videoSessionId: number | null;
  interactions: 'LIVE' | 'NONE';
  interactionsSessionId: number | null;
};

export const INITIAL_EVERGREEN_SOURCES: EvergreenSourcesSelection = {
  video: 'LIVE',
  videoSessionId: null,
  interactions: 'LIVE',
  interactionsSessionId: null,
};

export type EvergreenSourcesIssue =
  'VIDEO_REQUIRED' | 'INTERACTIONS_SESSION_REQUIRED' | 'MP4_IMPORT_REQUIRED';

/**
 * Le Live choisi pour la vidéo devient aussi la source des interactions tant qu'aucune n'est
 * choisie. Un Live ou « Partir de zéro » déjà choisi pour les interactions reste en place.
 */
export function selectVideoSession(
  selection: EvergreenSourcesSelection,
  sessionId: number | null,
): EvergreenSourcesSelection {
  if (sessionId == null) return { ...selection, videoSessionId: null };
  if (selection.interactions === 'NONE' || selection.interactionsSessionId != null) {
    return { ...selection, video: 'LIVE', videoSessionId: sessionId };
  }
  return {
    video: 'LIVE',
    videoSessionId: sessionId,
    interactions: 'LIVE',
    interactionsSessionId: sessionId,
  };
}

/** Passer sur une vidéo MP4 ne touche jamais aux interactions. */
export function selectVideoKind(
  selection: EvergreenSourcesSelection,
  video: EvergreenSourcesSelection['video'],
): EvergreenSourcesSelection {
  return { ...selection, video };
}

export function selectInteractionsKind(
  selection: EvergreenSourcesSelection,
  interactions: EvergreenSourcesSelection['interactions'],
): EvergreenSourcesSelection {
  return { ...selection, interactions };
}

export function selectInteractionsSession(
  selection: EvergreenSourcesSelection,
  sessionId: number | null,
): EvergreenSourcesSelection {
  return { ...selection, interactions: 'LIVE', interactionsSessionId: sessionId };
}

export function findSourcesIssue(
  selection: EvergreenSourcesSelection,
  hasMp4Video: boolean,
): EvergreenSourcesIssue | null {
  const hasVideo = selection.video === 'LIVE' ? selection.videoSessionId != null : hasMp4Video;
  if (!hasVideo) return 'VIDEO_REQUIRED';
  if (selection.interactions === 'LIVE' && selection.interactionsSessionId == null) {
    return 'INTERACTIONS_SESSION_REQUIRED';
  }
  return null;
}

export function buildInteractionSourceInput(
  selection: EvergreenSourcesSelection,
): EvergreenInteractionSourceInput | null {
  if (selection.interactions === 'NONE') return { interactionSourceType: 'NONE' };
  if (selection.interactionsSessionId == null) return null;
  return {
    interactionSourceType: 'LIVE',
    interactionSourceSessionId: selection.interactionsSessionId,
  };
}

/** Sans source d'interactions, le back conserve celle en place. */
export function buildMp4SourcesInput(
  videoResourceId: number,
  interactions: EvergreenInteractionSourceInput | null,
): EvergreenSourcesInput {
  return { videoSourceType: 'MP4', videoResourceId, ...interactions };
}

export function buildLiveVideoSourcesInput(
  selection: EvergreenSourcesSelection,
): EvergreenSourcesInput | null {
  const interactions = buildInteractionSourceInput(selection);
  if (selection.video !== 'LIVE' || selection.videoSessionId == null || !interactions) return null;
  return {
    videoSourceType: 'LIVE',
    videoSourceSessionId: selection.videoSessionId,
    ...interactions,
  };
}

/**
 * Un MP4 importé prime sur le Live source : un ancien remplacement de vidéo garde son Live, et un
 * Live YouTube en attente de son MP4 n'a pas encore de vidéo.
 */
export function selectionFromConfiguration(
  configuration: Pick<
    EvergreenConfiguration,
    'videoSourceSession' | 'interactionSourceSession' | 'videoResourceId' | 'awaitingImportedMp4'
  >,
): EvergreenSourcesSelection {
  const liveVideoSessionId =
    configuration.videoResourceId == null && !configuration.awaitingImportedMp4
      ? (configuration.videoSourceSession?.id ?? null)
      : null;
  const interactionsSessionId = configuration.interactionSourceSession?.id ?? null;
  return {
    video: liveVideoSessionId != null ? 'LIVE' : 'MP4',
    videoSessionId: liveVideoSessionId,
    interactions: interactionsSessionId != null ? 'LIVE' : 'NONE',
    interactionsSessionId,
  };
}

type SavedSources = Pick<
  EvergreenConfiguration,
  'videoSourceSession' | 'interactionSourceSession' | 'videoResourceId' | 'awaitingImportedMp4'
>;

/** Une vidéo enregistrée fige les sources ; avant, elles restent à choisir (import échoué, YouTube). */
export function hasSavedEvergreenSources(configuration: SavedSources | null): boolean {
  if (!configuration) return false;
  return (
    configuration.videoResourceId != null ||
    (configuration.videoSourceSession != null && !configuration.awaitingImportedMp4)
  );
}

/**
 * Source d'interactions à envoyer avec la vidéo. Inchangée par rapport à l'enregistrement, elle est
 * omise : le back conserve alors la timeline en place et lance l'import d'un Live YouTube en attente.
 */
export function interactionSourceToSave(
  selection: EvergreenSourcesSelection,
  saved: Pick<EvergreenConfiguration, 'interactionSourceSession'> | null,
): EvergreenInteractionSourceInput | null {
  const interactions = buildInteractionSourceInput(selection);
  if (!saved) return interactions;
  const savedSessionId = saved.interactionSourceSession?.id ?? null;
  const unchanged =
    selection.interactions === 'NONE'
      ? savedSessionId == null
      : selection.interactionsSessionId === savedSessionId;
  return unchanged ? null : interactions;
}

function savedInteractionsSessionId(saved: Pick<SavedSources, 'interactionSourceSession'>) {
  return saved.interactionSourceSession?.id ?? null;
}

function chosenInteractionsSessionId(selection: EvergreenSourcesSelection): number | null {
  return selection.interactions === 'NONE' ? null : selection.interactionsSessionId;
}

/** Ce que le back retient quand la section interactions est omise : le Live de la vidéo, sinon l'existant. */
function interactionsSessionIdIfOmitted(
  selection: EvergreenSourcesSelection,
  saved: Pick<SavedSources, 'interactionSourceSession'>,
): number | null {
  if (selection.video === 'LIVE' && selection.videoSessionId != null) {
    return selection.videoSessionId;
  }
  return savedInteractionsSessionId(saved);
}

/**
 * Section interactions d'une modification : omise seulement quand l'omettre donne exactement le
 * choix affiché. Garder « Partir de zéro » avec une vidéo Live exige donc un NONE explicite.
 */
export function interactionSourceToSend(
  selection: EvergreenSourcesSelection,
  saved: Pick<SavedSources, 'interactionSourceSession'>,
): EvergreenInteractionSourceInput | null {
  if (chosenInteractionsSessionId(selection) === interactionsSessionIdIfOmitted(selection, saved)) {
    return null;
  }
  return buildInteractionSourceInput(selection);
}

/** Effet d'un enregistrement sur les interactions des prochaines sessions. */
export type EvergreenInteractionsEffect = 'KEEP' | 'IMPORT_LIVE' | 'RESET';

export type EvergreenSourcesSavePlan =
  | { kind: 'unchanged' }
  | { kind: 'issue'; issue: EvergreenSourcesIssue }
  | {
      kind: 'save';
      input: EvergreenSourcesInput;
      interactionsEffect: EvergreenInteractionsEffect;
      interactionsSessionId: number | null;
    };

/**
 * Modification des sources d'un Evergreen déjà configuré. Une nouvelle vidéo MP4 passe par l'import
 * (enregistré à part) : ici, une vidéo MP4 est forcément celle déjà enregistrée.
 */
export function planEvergreenSourcesSave(
  selection: EvergreenSourcesSelection,
  saved: SavedSources,
): EvergreenSourcesSavePlan {
  const savedSelection = selectionFromConfiguration(saved);
  const savedMp4ResourceId = savedSelection.video === 'MP4' ? saved.videoResourceId : null;
  let video: EvergreenVideoSourceInput;
  if (selection.video === 'MP4') {
    if (savedMp4ResourceId == null) return { kind: 'issue', issue: 'MP4_IMPORT_REQUIRED' };
    video = { videoSourceType: 'MP4', videoResourceId: savedMp4ResourceId };
  } else {
    if (selection.videoSessionId == null) return { kind: 'issue', issue: 'VIDEO_REQUIRED' };
    video = { videoSourceType: 'LIVE', videoSourceSessionId: selection.videoSessionId };
  }
  const issue = findSourcesIssue(selection, true);
  if (issue) return { kind: 'issue', issue };

  const interactionsSessionId = chosenInteractionsSessionId(selection);
  const replacesInteractions = interactionsSessionId !== savedInteractionsSessionId(saved);
  const videoUnchanged =
    video.videoSourceType === 'MP4' ||
    (savedSelection.video === 'LIVE' && savedSelection.videoSessionId === selection.videoSessionId);
  if (videoUnchanged && !replacesInteractions) return { kind: 'unchanged' };

  const interactions = interactionSourceToSend(selection, saved);
  return {
    kind: 'save',
    input: { ...video, ...interactions },
    interactionsEffect: !replacesInteractions
      ? 'KEEP'
      : interactionsSessionId == null
        ? 'RESET'
        : 'IMPORT_LIVE',
    interactionsSessionId,
  };
}

/** Le choix d'interactions affiché diffère de celui enregistré. */
export function isInteractionsSelectionDirty(
  selection: EvergreenSourcesSelection,
  saved: SavedSources,
): boolean {
  const savedSelection = selectionFromConfiguration(saved);
  return (
    selection.interactions !== savedSelection.interactions ||
    chosenInteractionsSessionId(selection) !== chosenInteractionsSessionId(savedSelection)
  );
}

export function isSourcesSelectionDirty(
  selection: EvergreenSourcesSelection,
  saved: SavedSources,
): boolean {
  const savedSelection = selectionFromConfiguration(saved);
  if (selection.video !== savedSelection.video) return true;
  if (selection.video === 'LIVE' && selection.videoSessionId !== savedSelection.videoSessionId) {
    return true;
  }
  return isInteractionsSelectionDirty(selection, saved);
}

/**
 * Identité des sources enregistrées : le formulaire ne se réaligne que quand elle change, pas à
 * chaque rechargement de la configuration (sinon un choix en cours serait effacé).
 */
export function savedSourcesKey(saved: SavedSources | null): string {
  if (!saved) return '';
  return [
    saved.videoSourceSession?.id ?? '-',
    saved.videoResourceId ?? '-',
    saved.interactionSourceSession?.id ?? '-',
    saved.awaitingImportedMp4 ? 'awaiting' : 'set',
  ].join('|');
}

export type EvergreenInteractionsStatus = 'ready' | 'importing' | 'blocked';

/** État des interactions d'un Evergreen configuré ; null tant qu'aucune vidéo n'est enregistrée. */
export function resolveInteractionsStatus(
  configuration:
    | (SavedSources &
        Pick<
          EvergreenConfiguration,
          'ready' | 'conversionTotalItems' | 'conversionProcessedItems' | 'interactionsOutsideVideo'
        >)
    | null,
): EvergreenInteractionsStatus | null {
  if (!configuration || !hasSavedEvergreenSources(configuration)) return null;
  const outcome = resolveConversionOutcome(configuration);
  if (outcome.kind === 'ready') return 'ready';
  return outcome.kind === 'blocked' ? 'blocked' : 'importing';
}

export type EvergreenConfigurationAvailability =
  'NOT_REQUIRED' | 'LOADED' | 'LOADING' | 'UNAVAILABLE';

/**
 * Un Evergreen déjà configuré ne s'enregistre qu'une fois sa configuration chargée : avant, le
 * formulaire n'affiche que des sources provisoires et les envoyer remplacerait ses interactions.
 */
export function resolveEvergreenConfigurationAvailability({
  isConfiguredEvergreen,
  configurationLoaded,
  loadFailed,
}: {
  isConfiguredEvergreen: boolean;
  configurationLoaded: boolean;
  loadFailed: boolean;
}): EvergreenConfigurationAvailability {
  if (!isConfiguredEvergreen) return 'NOT_REQUIRED';
  if (configurationLoaded) return 'LOADED';
  return loadFailed ? 'UNAVAILABLE' : 'LOADING';
}

export type Mp4ReplacementBlock = 'INTERACTIONS_DIRTY' | 'INTERACTIONS_IMPORTING';

/**
 * Un remplacement MP4 s'enregistre aussitôt : il effacerait un choix d'interactions en cours, et
 * pendant un import d'interactions la durée de référence des coupures n'est pas encore fiable.
 */
export function findMp4ReplacementBlock({
  interactionsDirty,
  interactionsStatus,
}: {
  interactionsDirty: boolean;
  interactionsStatus: EvergreenInteractionsStatus | null;
}): Mp4ReplacementBlock | null {
  if (interactionsStatus === 'importing') return 'INTERACTIONS_IMPORTING';
  return interactionsDirty ? 'INTERACTIONS_DIRTY' : null;
}

/** Nom d'un Live source : son titre, à défaut son identifiant connu, à défaut son numéro. */
export function resolveSourceSessionLabel(
  webinars: readonly WebinarForEvergreen[],
  sessionId: number,
  knownSessions: readonly (EvergreenSourceSession | null | undefined)[],
): string {
  const title = findPastSession(webinars, sessionId)?.webinar.title;
  if (title) return title;
  const known = knownSessions.find((session) => session?.id === sessionId);
  return known?.identifier ?? `#${sessionId}`;
}

/** Sources retenues à l'étape Session, enregistrées une fois le webinaire créé. */
export type EvergreenSourceDraft = {
  video: { kind: 'LIVE'; sourceSessionId: number } | { kind: 'MP4'; file: File };
  interactions: EvergreenInteractionSourceInput;
};

export function buildSourceDraft(
  selection: EvergreenSourcesSelection,
  mp4File: File | null,
): EvergreenSourceDraft | null {
  const interactions = buildInteractionSourceInput(selection);
  if (!interactions) return null;
  if (selection.video === 'MP4') {
    return mp4File ? { video: { kind: 'MP4', file: mp4File }, interactions } : null;
  }
  if (selection.videoSessionId == null) return null;
  return { video: { kind: 'LIVE', sourceSessionId: selection.videoSessionId }, interactions };
}

export function selectionFromDraft(
  draft: Pick<EvergreenSourceDraft, 'interactions'> & {
    video: { kind: 'LIVE'; sourceSessionId: number } | { kind: 'MP4' };
  },
): EvergreenSourcesSelection {
  return {
    video: draft.video.kind,
    videoSessionId: draft.video.kind === 'LIVE' ? draft.video.sourceSessionId : null,
    interactions: draft.interactions.interactionSourceType,
    interactionsSessionId:
      draft.interactions.interactionSourceType === 'LIVE'
        ? draft.interactions.interactionSourceSessionId
        : null,
  };
}

const SOURCES_SAVE_ERRORS: Readonly<Record<string, EvergreenMp4UploadError>> = {
  'HTTP.ERROR.403.EVERGREEN_SESSION_IN_PROGRESS': 'EVERGREEN_SESSION_IN_PROGRESS',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.DURATION_MISMATCH': 'CUTS_DURATION_MISMATCH',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.INTERACTION_OUTSIDE_VIDEO_DURATION':
    'INTERACTION_OUTSIDE_VIDEO_DURATION',
  'HTTP.ERROR.400.EVERGREEN.MP4.INTERACTION_OUTSIDE_VIDEO_DURATION':
    'INTERACTION_OUTSIDE_VIDEO_DURATION',
  'HTTP.ERROR.400.EVERGREEN.VIDEO_SOURCE.INTERACTION_OUTSIDE_VIDEO_DURATION':
    'INTERACTION_OUTSIDE_VIDEO_DURATION',
  ...SOURCE_SESSION_REFUSALS,
};

/**
 * Refus de l'enregistrement d'une vidéo MP4 validée, traduit en erreur affichable. Le code est lu à
 * la racine comme dans le détail d'une erreur de validation.
 */
export function toSourcesSaveError(error: unknown): EvergreenMp4UploadError {
  if (isEvergreenSourceTimelinePreparing(error)) return 'SOURCE_TIMELINE_PREPARING';
  for (const code of getBackErrorCodes(error)) {
    const known = SOURCES_SAVE_ERRORS[code];
    if (known) return known;
  }
  return 'SOURCES_SAVE_FAILED';
}

export type SourcesEditError =
  | 'EVERGREEN_SESSION_IN_PROGRESS'
  | 'INTERACTION_OUTSIDE_VIDEO_DURATION'
  | 'SOURCE_TIMELINE_PREPARING'
  | 'SOURCE_SESSION_DURATION_EXCEEDED'
  | 'SOURCE_SESSION_NOT_STARTED'
  | 'SOURCE_VIDEO_DURATION_UNAVAILABLE'
  | 'GENERIC';

/** Refus d'une modification des sources depuis le formulaire (vidéo Live ou interactions). */
export function toSourcesEditError(error: unknown): SourcesEditError {
  const saveError = toSourcesSaveError(error);
  switch (saveError) {
    case 'EVERGREEN_SESSION_IN_PROGRESS':
    case 'INTERACTION_OUTSIDE_VIDEO_DURATION':
    case 'SOURCE_TIMELINE_PREPARING':
    case 'SOURCE_SESSION_DURATION_EXCEEDED':
    case 'SOURCE_SESSION_NOT_STARTED':
    case 'SOURCE_VIDEO_DURATION_UNAVAILABLE':
      return saveError;
    default:
      return 'GENERIC';
  }
}

/** Le back refuse un Live YouTube comme vidéo : Welya n'en a pas l'enregistrement. */
export function isVideoSourceEligible(session: Pick<PastSession, 'transmissionMode'>): boolean {
  return session.transmissionMode !== 'YOUTUBE';
}

export function findPastSession(
  webinars: readonly WebinarForEvergreen[],
  sessionId: number | null,
): { webinar: WebinarForEvergreen; session: PastSession } | null {
  if (sessionId == null) return null;
  for (const webinar of webinars) {
    const session = webinar.finishedSessions.find((candidate) => candidate.id === sessionId);
    if (session) return { webinar, session };
  }
  return null;
}
